import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { LoginPanel } from "@/components/AuthPanel";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { Layout, Splash } from "@/components/Layout";
import SignIn from "@/pages/SignIn";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Book from "@/pages/Book";
import History from "@/pages/History";
import BookingDetail from "@/pages/BookingDetail";
import BookingEdit from "@/pages/BookingEdit";
import Leaderboard from "@/pages/Leaderboard";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import Impact from "@/pages/Impact";
import Notifications from "@/pages/Notifications";
import Help from "@/pages/Help";
import ComplaintDetail from "@/pages/ComplaintDetail";
import Settings from "@/pages/Settings";
import Kyc from "@/pages/Kyc";
import Learn from "@/pages/Learn";
import News from "@/pages/News";
import Compliance from "@/pages/Compliance";
import Rewards from "@/pages/Rewards";
import Addresses from "@/pages/Addresses";
import Rates from "@/pages/Rates";
import { LiveBridge } from "@/components/NativeShell";
import AdminLogin from "@/admin/AdminLogin";
import AdminDashboard from "@/admin/AdminDashboard";
import Partner, { PartnerLogin, PartnerJoin } from "@/pages/Partner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetStatus } from "@/components/NetStatus";

const Static = ({ to }) => { window.location.replace(to); return null; };

const RequireLogin = () => {
  const { user, t } = useApp();
  const loc = useLocation();
  if (user === null) return <Splash />;
  if (!user)
    return (
      <div className="space-y-5" data-testid="login-required">
        <h1 className="font-display text-3xl font-extrabold text-[#0D3B2B]">{t("Log in")}</h1>
        <p className="text-sm text-[#4A6358]">{t("See your pickups, impact and rank.")}</p>
        <LoginPanel afterPath={loc.pathname} />
      </div>
    );
  return <Outlet />;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/login" element={<SignIn />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/partner" element={<PartnerLogin />} />
      <Route path="/partner/join" element={<PartnerJoin />} />
      <Route path="/partner/:token" element={<Partner />} />
      <Route path="/onboarding" element={<Navigate to="/profile/edit" replace />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<Book />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/news" element={<News />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/rates" element={<Rates />} />
        <Route path="/privacy" element={<Static to="/privacy.html" />} />
        <Route path="/terms" element={<Static to="/terms.html" />} />
        <Route element={<RequireLogin />}>
          <Route path="/history" element={<History />} />
          <Route path="/history/:id" element={<BookingDetail />} />
          <Route path="/history/:id/edit" element={<BookingEdit />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/edit" element={<EditProfile />} />
          <Route path="/profile/kyc" element={<Kyc />} />
          <Route path="/profile/addresses" element={<Addresses />} />
          <Route path="/impact" element={<Impact />} />
          <Route path="/rewards" element={<Rewards />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/help" element={<Help />} />
          <Route path="/help/:cid" element={<ComplaintDetail />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <NetStatus />
        <LiveBridge />
      </ErrorBoundary>
      <Toaster position="top-center" richColors closeButton toastOptions={{ style: { borderRadius: 16 } }} />
    </AppProvider>
  );
}
